import { IntlShape } from 'react-intl'
import {
  OperatingRoomEvolutionDTO,
  OperatingRoomEvolutionPointDTO,
} from '../../dto/operatingRoomIndicators/OperatingRoomIndicatorsDTO'
import { markPeaks, PeakAnalysis } from '../ServicesStatistics/peaks'
import { parseIsoDate, TimelinePoint } from '../ServicesStatistics/statisticsUtils'
import { chipTitle, periodTitle } from '../ServicesStatistics/TimelineChart'

/**
 * Evolución de la actividad de quirófano. Funciones puras, sin React: los picos y los promedios se calculan en el front con
 * los puntos que llegaron de `/evolution` (un punto por día o por semana).
 */

/** Lo que se mide en el gráfico: el mismo dato en otra vista (SelectButton «Cirugías | Horas | Ocupación»). */
export type OperatingRoomMeasure = 'surgeries' | 'hours' | 'occupancy'

export const OPERATING_ROOM_MEASURES: OperatingRoomMeasure[] = ['surgeries', 'hours', 'occupancy']

/**
 * Horas de agenda mínimas de un punto para que su ocupación se tome en serio (marcador pleno y chance de ser pico): una
 * jornada de 8 h, un bloque típico. Con menos capacidad (una sala con un solo bloque corto, un día de poca agenda) un par de
 * horas mueven el porcentaje 25 puntos o más. Es el piso que le pasamos a `markPeaks` (que además exige al menos el 25 % de la
 * mediana de capacidad por punto, así que con semanas la barra sube sola). A VALIDAR por el servicio.
 */
export const EVOLUTION_MIN_CAPACITY_HOURS = 8

/** El valor de la medida en el punto; null si no hay con qué calcularlo (una ocupación sin agenda no es 0 %). */
export function pointValue(point: OperatingRoomEvolutionPointDTO, measure: OperatingRoomMeasure): number | null {
  if (measure === 'occupancy') return point.occupancyPct
  return measure === 'hours' ? point.hoursUsed : point.surgeries
}

/** «Hoy» del servidor (`asOf`, `yyyy-MM-ddTHH:mm:ss`) como día. */
export const asOfDay = (asOf: string): string => asOf.substring(0, 10)

/** El punto todavía no está cerrado: incluye el día en curso (o uno futuro). */
export function isOpenPoint(point: OperatingRoomEvolutionPointDTO, asOf: string): boolean {
  return point.endDate >= asOfDay(asOf)
}

/**
 * Marcador hueco: el punto no está cerrado, o, con la ocupación, tiene poca agenda (el % puede no ser representativo).
 */
export function isLowVolume(point: OperatingRoomEvolutionPointDTO, measure: OperatingRoomMeasure, asOf: string): boolean {
  return isOpenPoint(point, asOf)
    || (measure === 'occupancy' && point.capacityHours > 0 && point.capacityHours < EVOLUTION_MIN_CAPACITY_HOURS)
}

/**
 * Picos de la ocupación con el criterio de `markPeaks` (media + 2 desvíos entre los puntos que cuentan, al menos 7, como mucho
 * 3) y las horas de agenda como base. Los puntos sin cerrar no cuentan. Con «Cirugías» y «Horas» no hay picos: el volumen sube y
 * baja con la semana (los fines de semana se opera menos) y eso no es un problema.
 */
export function occupancyPeaks(points: OperatingRoomEvolutionPointDTO[], asOf: string): PeakAnalysis {
  return markPeaks(
    points.map((point) => ({
      value: isOpenPoint(point, asOf) ? null : point.occupancyPct,
      attentions: point.capacityHours,
    })),
    EVOLUTION_MIN_CAPACITY_HOURS,
  )
}

const dayCount = (point: OperatingRoomEvolutionPointDTO): number =>
  Math.round((parseIsoDate(point.endDate).getTime() - parseIsoDate(point.date).getTime()) / 86400000) + 1

/**
 * La línea de promedio de «Cirugías» y «Horas»: el total sobre los puntos cerrados y completos (una semana de menos de 7 días,
 * la primera o la última del período, no se toma: bajaría el promedio). Por día con puntos diarios; por semana con semanales.
 * Con «Ocupación» la línea es la del indicador (ponderada), no un promedio de porcentajes.
 */
export function averagePerPoint(
  evolution: OperatingRoomEvolutionDTO,
  measure: 'surgeries' | 'hours',
  asOf: string,
): number | null {
  const full = evolution.points.filter((point) => !isOpenPoint(point, asOf)
    && (evolution.granularity === 'DAY' || dayCount(point) === 7))
  if (full.length === 0) return null
  return full.reduce((sum, point) => sum + (pointValue(point, measure) ?? 0), 0) / full.length
}

/** El punto como período del eje de tiempo compartido (título, chip y rótulos de las otras pantallas de reportes). */
function timelinePoint(point: OperatingRoomEvolutionPointDTO): TimelinePoint {
  const start = parseIsoDate(point.date)
  return { period: point.date, start, end: parseIsoDate(point.endDate), attentions: 0, avgWait: null, avgService: null }
}

/** Título completo del punto: «Sábado 19/09/2026» o «Semana del 07/09 al 13/09». */
export function pointTitleOf(
  intl: IntlShape,
  point: OperatingRoomEvolutionPointDTO,
  granularity: 'DAY' | 'WEEK',
): string {
  return periodTitle(intl, timelinePoint(point), granularity)
}

/** Nombre del punto en un chip: «Sábado 19/09» o «Semana del 07/09 al 13/09». */
export function pointChipOf(
  intl: IntlShape,
  point: OperatingRoomEvolutionPointDTO,
  granularity: 'DAY' | 'WEEK',
): string {
  return chipTitle(intl, timelinePoint(point), granularity)
}
